import { Head, Link } from '@inertiajs/react';

/**
 * Comic data from the backend.
 */
interface NewspaperComic {
    id: number;
    title: string | null;
    slug: string;
    artist_name: string;
    artist_url: string | null;
    thumbnail_url: string | null;
    images_count: number;
    is_published: boolean;
    published_at: string | null;
}

/**
 * Pagination link from Laravel.
 */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/**
 * Paginated comics response.
 */
interface PaginatedComics {
    data: NewspaperComic[];
    links: PaginationLink[];
    last_page: number;
}

/**
 * Props for the Comics Index page.
 */
interface Props {
    comics: PaginatedComics;
    isGm: boolean;
}

/**
 * Public Comics Index page - archive of all published comics.
 * Uses traditional newspaper styling with white background and black text.
 */
export default function Index({ comics, isGm }: Props) {
    return (
        <>
            <Head title="Features - The Feywild Times" />

            <div className="min-h-screen bg-gray-50">
                {/* Newspaper Header */}
                <header className="bg-white border-b-4 border-double border-gray-900 py-6">
                    <div className="max-w-6xl mx-auto px-4 text-center">
                        <Link href="/newspaper" className="text-gray-600 hover:text-gray-500 text-sm mb-2 inline-block">
                            ← Back to The Feywild Times
                        </Link>
                        <h1 className="text-4xl md:text-5xl font-serif font-bold text-black tracking-tight">
                            Features
                        </h1>
                        <p className="text-gray-600 font-serif italic mt-2">
                            Illustrated tales from the Faewild
                        </p>
                    </div>
                </header>

                {/* Main Content */}
                <main className="max-w-6xl mx-auto px-4 py-6">
                    {comics.data.length === 0 ? (
                        <EmptyComics />
                    ) : (
                        <>
                            {/* Comics Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {comics.data.map((comic) => (
                                    <ComicCard key={comic.id} comic={comic} isGm={isGm} />
                                ))}
                            </div>

                            {/* Pagination */}
                            {comics.last_page > 1 && (
                                <Pagination links={comics.links} />
                            )}
                        </>
                    )}
                </main>
            </div>
        </>
    );
}

/**
 * Empty state when no comics exist.
 */
function EmptyComics() {
    return (
        <div className="text-center py-16">
            <div className="text-6xl mb-6">🎨</div>
            <h2 className="text-2xl font-serif text-black mb-4">
                Features Coming Soon!
            </h2>
            <p className="text-gray-700 max-w-lg mx-auto">
                Our artists are hard at work. Check back soon for illustrated tales
                from across the Faewild realm.
            </p>
            <Link
                href="/newspaper"
                className="inline-block mt-6 text-gray-700 hover:text-gray-500 underline"
            >
                ← Return to The Feywild Times
            </Link>
        </div>
    );
}

/**
 * Comic card - displays a single comic in the grid.
 */
function ComicCard({ comic, isGm }: { comic: NewspaperComic; isGm: boolean }) {
    const displayTitle = comic.title || `Feature by ${comic.artist_name}`;

    return (
        <Link
            href={`/newspaper/comics/${comic.slug}`}
            className="block bg-white border border-gray-300 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
        >
            {/* Thumbnail */}
            <div className="aspect-square bg-gray-100 relative">
                {comic.thumbnail_url ? (
                    <img
                        src={comic.thumbnail_url}
                        alt={displayTitle}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                        🎨
                    </div>
                )}
                {/* Draft badge for GMs */}
                {isGm && !comic.is_published && (
                    <span className="absolute top-2 right-2 px-2 py-0.5 bg-yellow-500 text-white rounded text-xs">
                        Draft
                    </span>
                )}
            </div>

            {/* Info */}
            <div className="p-3">
                <h3 className="font-serif font-bold text-black text-sm line-clamp-2 leading-tight">
                    {displayTitle}
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                    by {comic.artist_name}
                </p>
            </div>
        </Link>
    );
}

/**
 * Pagination - Displays pagination links with newspaper styling.
 */
function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-6 flex justify-center gap-1">
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1 rounded text-sm font-serif ${
                        link.active
                            ? 'bg-gray-800 text-white'
                            : link.url
                            ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
        </div>
    );
}

